import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'

import { GET, httpOk } from 'helpers/api'
import SmsCounter from 'pages/parts/SmsCounter'

/**
 * Выбор шаблона Mison SMS вместо свободного текста.
 *
 * Оператор берёт обычную цену только за текст, зарегистрированный у него как шаблон;
 * любой другой текст в ~20 раз дороже. Поэтому у провайдера Mison SMS заведение не пишет
 * текст, а выбирает шаблон из каталога Mison и заполняет поля (бонусы, скидка, дата…).
 * Текст собирает сервер — здесь только предпросмотр.
 *
 * Если у бренда свой провайдер (не Mison SMS), компонент ничего не рисует и сообщает
 * onModeChange(false) — экран работает по-старому, со своими шаблонами заведения.
 *
 * value: { template_code, vars: {}, locale: 'auto' | 'ru' | 'uz_latn' }
 */

const NAME_FALLBACK = {
	ru: 'Уважаемый гость',
	uz_latn: 'Hurmatli mehmon',
}

const INPUT_TYPES = {
	number: { type: 'number', min: 0, step: 1 },
	percent: { type: 'number', min: 1, max: 100, step: 1 },
	int: { type: 'number', min: 1, step: 1 },
	date: { type: 'date' },
	time: { type: 'time' },
	phone: { type: 'tel' },
	promo: { type: 'text' },
	text: { type: 'text' },
}

// Предпросмотр значения так же, как его подставит сервер (SmsTemplateService::normalizeValue)
function previewValue(variable, raw) {
	const value = String(raw ?? '').trim()
	if (value === '') return `{${variable.name}}`

	if (variable.type === 'number') {
		const number = Number(value.replace(/\s/g, '').replace(',', '.'))
		return Number.isFinite(number) ? Math.round(number).toLocaleString('ru-RU').replace(/ /g, ' ') : value
	}
	if (variable.type === 'date') {
		const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
		return m ? `${m[3]}.${m[2]}.${m[1]}` : value
	}
	if (variable.type === 'promo') return value.toUpperCase()
	if (variable.type === 'phone') {
		const digits = value.replace(/\D/g, '')
		return digits.length >= 11 ? `+${digits}` : digits
	}

	return value
}

export function renderSmsPreview(template, locale, vars, brand, exampleName) {
	const text = template?.texts?.[locale]?.text || ''
	const variables = template?.variables || []

	return text.replace(/\{([a-z_]+)\}/g, (match, name) => {
		if (name === 'brand') return brand || match
		if (name === 'name') return exampleName || NAME_FALLBACK[locale] || NAME_FALLBACK.ru
		const variable = variables.find(item => item.name === name)

		return variable ? previewValue(variable, vars?.[name]) : match
	})
}

function SmsCatalogPicker({ usage = 'marketing', value, onChange, onModeChange, allowGuestLocale = true, exampleName = null }) {
	const { t } = useTranslation()

	const [catalog, setCatalog] = useState({ loaded: false, is_template_only: false, brand: '', data: [] })

	useEffect(() => {
		let active = true

		async function load() {
			const response = await GET(`/sms/catalog?usage=${usage}`)
			if (!active) return
			if (httpOk(response)) {
				const data = response.data || {}
				setCatalog({ loaded: true, is_template_only: !!data.is_template_only, brand: data.brand || '', data: data.data || [] })
				if (onModeChange) onModeChange(!!data.is_template_only)
			} else {
				setCatalog({ loaded: true, is_template_only: false, brand: '', data: [] })
				if (onModeChange) onModeChange(false)
			}
		}

		load()
		return () => { active = false }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [usage])

	const selected = useMemo(
		() => catalog.data.find(item => item.code === value?.template_code) || null,
		[catalog.data, value?.template_code]
	)

	const options = useMemo(() => {
		const groups = {}
		catalog.data.forEach(item => {
			if (!groups[item.group_code]) groups[item.group_code] = []
			groups[item.group_code].push(item)
		})

		return Object.keys(groups).map(code => ({
			label: t(`sms_group_${code}`, code),
			options: groups[code],
		}))
	}, [catalog.data, t])

	if (!catalog.loaded || !catalog.is_template_only) {
		return null
	}

	const locale = value?.locale || (allowGuestLocale ? 'auto' : 'ru')
	const localeOptions = [
		...(allowGuestLocale ? [{ value: 'auto', label: t('sms_locale_guest') }] : []),
		{ value: 'ru', label: t('sms_locale_ru') },
		{ value: 'uz_latn', label: t('sms_locale_uz') },
	]

	// Предпросмотр: на выбранном языке; «на языке гостя» — оба варианта
	const previewLocales = locale === 'auto'
		? ['ru', 'uz_latn'].filter(code => selected?.texts?.[code])
		: [selected?.texts?.[locale] ? locale : Object.keys(selected?.texts || {})[0]].filter(Boolean)

	const inputVariables = (selected?.variables || []).filter(variable => variable.is_input)

	function update(patch) {
		onChange({ template_code: null, vars: {}, locale, ...value, ...patch })
	}

	return (
		<div>
			<div className="alert alert-info py-2 px-3 mb-2" style={{ fontSize: '12px' }}>
				{t('sms_catalog_hint')}
			</div>

			<div className="form-group">
				<label>{t('sms_template')}<span className="required-mark">*</span></label>
				<Select
					options={options}
					value={selected}
					onChange={(option) => update({ template_code: option ? option.code : null, vars: {} })}
					placeholder={t('sms_template_choose')}
					noOptionsMessage={() => t('sms_catalog_empty')}
					getOptionLabel={(option) => option.title}
					getOptionValue={(option) => option.code}
				/>
			</div>

			<div className="form-group">
				<label>{t('sms_language')}</label>
				<Select
					options={localeOptions}
					value={localeOptions.find(option => option.value === locale) || localeOptions[0]}
					onChange={(option) => update({ locale: option.value })}
					isSearchable={false}
				/>
			</div>

			{inputVariables.length > 0 &&
				<div className="row">
					{inputVariables.map(variable => {
						const inputType = INPUT_TYPES[variable.type] || INPUT_TYPES.text
						return (
							<div className="col-md-6" key={variable.name}>
								<div className="form-group">
									<label>{t(`sms_var_${variable.name}`, variable.label)}<span className="required-mark">*</span></label>
									<input className="form-control" required
										{...inputType}
										maxLength={inputType.type === 'text' || inputType.type === 'tel' ? variable.max : undefined}
										value={value?.vars?.[variable.name] ?? ''}
										onChange={(e) => update({ vars: { ...(value?.vars || {}), [variable.name]: e.target.value } })} />
								</div>
							</div>
						)
					})}
				</div>
			}

			{selected && previewLocales.map(code => {
				const text = renderSmsPreview(selected, code, value?.vars, catalog.brand, exampleName)
				return (
					<div className="form-group" key={code}>
						<label>{t('sms_preview')} — {code === 'ru' ? t('sms_locale_ru') : t('sms_locale_uz')}</label>
						<textarea className="form-control" rows="3" disabled readOnly value={text}></textarea>
						<SmsCounter text={text} />
					</div>
				)
			})}
		</div>
	)
}

export default SmsCatalogPicker
